import { memo, useCallback, useMemo, useState } from 'react';

import type { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import styled from 'styled-components';

import { Button, DatePicker, Dialog, KendoDropDownList } from 'core/ui';
import { ButtonVariants } from 'core/ui/Button/constants';
import { ComponentSizes } from 'core/ui/constants';

import { DateRangeFilterProps, DateRangeFilterType, DateRangeFilterValue } from './DateRangeFilterProps';

const RELATIVE_DATE_FILTER_OPTIONS = [
  { label: 'Custom Range', value: 'custom' as DateRangeFilterType },
  { label: 'Today', value: 'today' as DateRangeFilterType },
  { label: 'Yesterday', value: 'yesterday' as DateRangeFilterType },
  { label: 'This Week', value: 'this-week' as DateRangeFilterType },
  { label: 'Last Week', value: 'last-week' as DateRangeFilterType },
  { label: 'This Month', value: 'this-month' as DateRangeFilterType },
  { label: 'Last Month', value: 'last-month' as DateRangeFilterType },
  { label: 'Last 7 Days', value: 'last-7-days' as DateRangeFilterType },
  { label: 'Last 30 Days', value: 'last-30-days' as DateRangeFilterType },
  { label: 'Last 90 Days', value: 'last-90-days' as DateRangeFilterType },
];

export const DateRangeFilter = memo<DateRangeFilterProps>(
  ({ value, onChange, onClear, isOpen, onClose, title = 'Select Date Range', size = ComponentSizes.MEDIUM, disabled = false }) => {
    const [selectedType, setSelectedType] = useState<DateRangeFilterType | null>(value?.type || 'custom');
    const [fromDate, setFromDate] = useState<Date | null>(value?.fromDate || null);
    const [toDate, setToDate] = useState<Date | null>(value?.toDate || null);

    const handleTypeChange = useCallback((e: DropDownListChangeEvent) => {
      const newType = e.value as DateRangeFilterType;
      setSelectedType(newType);

      // Clear custom dates when switching away from custom
      if (newType !== 'custom') {
        setFromDate(null);
        setToDate(null);
      }
    }, []);

    const handleFromDateChange = useCallback(
      (e: { value: Date | null }) => {
        setFromDate(e.value);
        // Auto-switch to custom when setting dates manually
        if (selectedType !== 'custom') {
          setSelectedType('custom');
        }
      },
      [selectedType],
    );

    const handleToDateChange = useCallback(
      (e: { value: Date | null }) => {
        setToDate(e.value);
        // Auto-switch to custom when setting dates manually
        if (selectedType !== 'custom') {
          setSelectedType('custom');
        }
      },
      [selectedType],
    );

    const handleQuickCustom = useCallback(() => {
      setSelectedType('custom');
    }, []);

    const handleClear = useCallback(() => {
      setSelectedType(null);
      setFromDate(null);
      setToDate(null);
      onClear?.();
    }, [onClear]);

    const handleApply = useCallback(() => {
      if (!selectedType) return;

      const filterValue: DateRangeFilterValue = {
        type: selectedType,
        fromDate: selectedType === 'custom' ? fromDate : undefined,
        toDate: selectedType === 'custom' ? toDate : undefined,
      };

      onChange(filterValue);
      onClose();
    }, [selectedType, fromDate, toDate, onChange, onClose]);

    const canApply = useMemo(() => {
      if (!selectedType) return false;

      if (selectedType === 'custom') {
        if (!fromDate && !toDate) return false;
        if (fromDate && toDate && fromDate > toDate) return false;
      }

      return true;
    }, [selectedType, fromDate, toDate]);

    if (!isOpen) {
      return null;
    }

    return (
      <>
        <DialogBackdrop />
        <Dialog title={title} onClose={onClose}>
          <FilterContent>
            {/* Quick Custom Range Section */}
            <QuickSection>
              <SectionTitle>Quick Custom Range</SectionTitle>
              <CustomDateSection>
                <DateGroup>
                  <Label>From Date</Label>
                  <DatePicker value={fromDate} onChange={handleFromDateChange} size={size} disabled={disabled} placeholder="Select start date" />
                </DateGroup>
                <DateGroup>
                  <Label>To Date</Label>
                  <DatePicker value={toDate} onChange={handleToDateChange} size={size} disabled={disabled} placeholder="Select end date" />
                </DateGroup>
              </CustomDateSection>
              {fromDate && toDate && fromDate > toDate && <ErrorMessage>From date must be before or equal to the to date.</ErrorMessage>}
            </QuickSection>

            <Divider>
              <DividerLine />
              <DividerText>OR</DividerText>
              <DividerLine />
            </Divider>

            {/* Preset Options Section */}
            <FormSection>
              <SectionTitle>Preset Ranges</SectionTitle>
              <KendoDropDownList
                data={RELATIVE_DATE_FILTER_OPTIONS}
                textField="label"
                dataItemKey="value"
                value={selectedType}
                onChange={handleTypeChange}
                size={size}
                disabled={disabled}
              />
            </FormSection>

            <ButtonSection>
              <Button variant={ButtonVariants.SECONDARY} onClick={handleClear} size={size} disabled={disabled}>
                Clear Filter
              </Button>
              <Button variant={ButtonVariants.PRIMARY} onClick={handleApply} size={size} disabled={disabled || !canApply}>
                Apply Filter
              </Button>
            </ButtonSection>
          </FilterContent>
        </Dialog>
      </>
    );
  },
);

DateRangeFilter.displayName = 'DateRangeFilter';

const DialogBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
`;

const FilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing20};
  min-width: 500px;
`;

const QuickSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Label = styled.label`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing20};
  margin: ${({ theme }) => theme.space.spacing10} 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.borderBase};
`;

const DividerText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

const CustomDateSection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing20};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DateGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing10};
  flex: 1;
`;

const ButtonSection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.space.spacing20};
  border-top: 1px solid ${({ theme }) => theme.colors.borderBase};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  padding: ${({ theme }) => theme.space.spacing10};
  background-color: ${({ theme }) => theme.colors.palette.reds[0]};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.palette.reds[2]};
`;
